import { useContext, useEffect, useState } from "react";
import { Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { LocaleContext } from "../../../routing/LangRouter";

//import customized hooks
import { useFormValidations } from "../../../hooks/useFormValidations";
import useRoomManagement from "../../../hooks/useRoomManagement";
import useDebouncedInputChange from "../../../hooks/useDebouncedInputChange ";

//import utils
import { numberWithCommas } from "../../../utils/utility";

//import constants
import BASE_URL from "../../../constants/baseUrls";

//import view components
import PassengerFormOptions from "./passenger-form-options";
import PassengerForm from "./passengers-form";
import RoomsCount from "../../common/rooms-count/rooms-count";

//import styles
import "./passengers-forms.scss";

function PassengersFormContainer({
    formIndex,
    formType,
    passengerType,
    forms,
    form,
    formCount,
    IDcard,
    setIDcard,
    control,
    register,
    defaultValues,
    watch,
    setValue,
    getValues,
    resetField,
    errors,
    roomIndex,
    nestedName = "",
}) {
    //general variables
    const { locale } = useContext(LocaleContext);
    const { t } = useTranslation();

    //sessionStorage variables
    const hotelInformation = JSON.parse(sessionStorage.getItem("hotelInformation"));
    const hotelPassengers = JSON.parse(sessionStorage.getItem("hotelPassengers"));
    const { isHotelInternational, isFlightInternational, isAutomaticReserve } = JSON.parse(sessionStorage.getItem("generalInformation")) || {};

    //country list request
    // const [countrySearchValue, setCountrySearchValue] = useState("");
    const [countryList, setCountryList] = useState();
    const [nationalityList, setNationalityList] = useState();

    // transform defaultValues passengers nationality to full object for the default value of autocomplete inside the PassengerForm
    const formatPassengerDefaultvalue = (list, fieldName, matchKey, transform = v => v) => {
        if (!defaultValues?.passengers) return;
        defaultValues.passengers.forEach((passenger, index) => {
            if (passenger[fieldName]) {
                const match = list.find(opt => transform(opt[matchKey]) === passenger[fieldName]);
                if (match) {
                    setValue(`${nestedName}passengers.${index}.${fieldName}`, match);
                }
            }
        });
    };

    const getCountryList = async (searchInput = "") => {
        const response = await axios.get(`${BASE_URL}/api/v2/basic/countries/`, {
            params: {
                search: searchInput,
            },
        });
        const list = response.data.data.list;
        setCountryList(list);
        formatPassengerDefaultvalue(list, "passport_country", "parto_iata");
    };

    const getNationalityList = async (searchInput = "") => {
        const response = await axios.get(`${BASE_URL}/api/v2/basic/countries/`, {
            params: {
                search: searchInput,
            },
        });
        const list = response.data.data.list;
        setNationalityList(list);

        formatPassengerDefaultvalue(list, "nationality", "parto_iata");
    };

    useEffect(() => {
        getCountryList();
        getNationalityList();
    }, []);
    //handle destiantion city change
    const handleInputChange = useDebouncedInputChange(input => {
        getCountryList(input);
    }, 1000);
    const handleNationalityInputChange = useDebouncedInputChange(input => {
        getNationalityList(input);
    }, 1000);

    const FIELD_NAME_MAP = {
        passport_expire_date: "passport_exp_date",
        passport_number: "passport_no",
    };

    // transform defaultValues passengers to form names for BaseTextField inside the PassengerForm
    const setPassengerDefaultTextField = fieldName => {
        if (!defaultValues?.passengers) return;
        const formFieldName = FIELD_NAME_MAP[fieldName] ?? fieldName;
        defaultValues.passengers.forEach((passenger, index) => {
            if (passenger[fieldName]) {
                setValue(`${nestedName}passengers.${index}.${formFieldName}`, passenger[fieldName]);
            }
        });
    };

    //gender options
    const GENDER_OPTIONS = [
        { id: 1, name: t("male"), english_name: "male" },
        { id: 2, name: t("female"), english_name: "female" },
    ];

    formatPassengerDefaultvalue(GENDER_OPTIONS, "gender", "english_name", v => v.toUpperCase());

    const NAME_INFO_EN = [
        { id: 1, label: t("firstNameEn"), name: "english_first_name", fieldType: "text", type: "input", condition: "always", dir: "ltr" },
        { id: 2, label: t("lastNameEn"), name: "english_last_name", fieldType: "text", type: "input", condition: "always", dir: "ltr" },
    ];

    const NAME_INFO = [
        { id: 3, label: t("firstName"), name: "first_name", fieldType: "text", type: "input", condition: "nationalCode", dir: "rtl" },
        { id: 4, label: t("lastName"), name: "last_name", fieldType: "text", type: "input", condition: "nationalCode", dir: "rtl" },
    ];

    const CONTACT_INFO = [
        {
            id: 5,
            label: t("phoneNumber"),
            name: "phone_number",
            fieldType: "number",
            type: "input",
            condition: !isAutomaticReserve && "always",
            dir: "ltr",
        },

        { id: 6, label: t("email"), name: "email", fieldType: "text", type: "input", condition: !isAutomaticReserve && "always", dir: "ltr" },
    ];

    const IDENTIFICATION = [
        { id: 8, label: t("nationalCode"), name: "national_code", fieldType: "number", type: "input", condition: "nationalCode", dir: "ltr" },
        {
            id: 9,
            label: t("passportNumber"),
            name: "passport_no",
            fieldType: "text",
            type: "input",
            condition: isHotelInternational || isFlightInternational ? "always" : "passport",
            dir: "ltr",
        },
    ];

    setPassengerDefaultTextField("passport_number");

    const IDENTIFICATION_DETAILS = [
        {
            id: 10,
            label: t("passportCountry"),
            name: "passport_country",
            type: "select",
            options: countryList,
            condition: isFlightInternational ? "always" : "passport",
            dir: locale === "en" ? "ltr" : "rtl",
            setInputValue: handleInputChange,
        },
        {
            id: 11,
            label: t("passportExpire"),
            name: "passport_exp_date",
            type: "date",
            minDate: new Date(),
            isPersianCalendar: false,
            condition: isFlightInternational ? "always" : "passport",
            dir: locale === "en" ? "ltr" : "rtl",
        },
    ];

    setPassengerDefaultTextField("passport_expire_date");

    const NATIONALITY = [
        {
            id: 7,
            label: t("nationality"),
            name: "nationality",
            type: "select",
            options: nationalityList,
            condition: "always",
            dir: locale === "en" ? "ltr" : "rtl",
            setInputValue: handleNationalityInputChange,
        },
    ];

    // Hotel-specific fields
    const HOTEL_SPECIFIC_FIELDS = [{ id: 14, label: "", name: "roomCount", type: "popover", condition: "always" }];

    // Flight-specific fields
    const FLIGHT_SPECIFIC_FIELDS = [
        {
            id: 12,
            label: t("gender"),
            name: "gender",
            type: "select",
            options: GENDER_OPTIONS,
            condition: "always",
            dir: locale === "en" ? "ltr" : "rtl",
        },
        {
            id: 13,
            label: isFlightInternational ? t("birthdayEn") : t("birthday"),
            name: "birthdate",
            type: "date",
            condition: "always",
            maxDate: new Date(),
            isPersianCalendar: isFlightInternational ? false : true,
            dir: locale === "en" ? "ltr" : "rtl",
        },
    ];

    // Combine base fields with specific fields
    const HOTEL_FIELD_LIST = [...NAME_INFO, ...CONTACT_INFO, ...NATIONALITY, ...IDENTIFICATION, ...HOTEL_SPECIFIC_FIELDS];
    const FLIGHT_FIELD_LIST = [
        ...NAME_INFO_EN,
        ...CONTACT_INFO,
        ...IDENTIFICATION,
        ...IDENTIFICATION_DETAILS,
        ...NATIONALITY,
        ...FLIGHT_SPECIFIC_FIELDS,
    ];
    const INT_FLIGHT_FIELD_LIST = [...NAME_INFO_EN, ...FLIGHT_SPECIFIC_FIELDS, ...NATIONALITY, ...IDENTIFICATION, ...IDENTIFICATION_DETAILS];
    const INT_HOTEL_FIELD_LIST = [
        ...NAME_INFO_EN,
        ...FLIGHT_SPECIFIC_FIELDS.filter(list => list.name !== "birthdate"),
        hotelInformation?.nationality?.id === 1 && {
            id: 14,
            label: t("nationalCode"),
            name: "national_code",
            fieldType: "number",
            type: "input",
            condition: "always",
        },
    ];

    //form validations
    const formValidations = useFormValidations(getValues(`${nestedName}passengers`) || []);

    //handle card type change
    const handleIDCardChange = (index, type) => {
        setIDcard(currentIdCard => {
            const updatedIdCard = [...currentIdCard];
            updatedIdCard[index] = { nationalCode: type === "nationalCode" ? true : false, passport: type === "passport" ? true : false };
            return updatedIdCard;
        });
        if (formType !== "intFlight") {
            resetField(`passengers.${index}.${type === "nationalCode" ? "passport_no" : "national_code"}`);
        }
    };

    //rooms count
    const { handleRoomCountChange, extendPrice, infant, totalPersons } = useRoomManagement(formCount, forms, setValue);

    return (
        <Grid key={form.id} container item xs={12} rowGap={{ xs: 2, md: 5 }} justifyContent="space-between">
            <span className="passengers-forms__room-name">{formCount?.[formType === "intHotel" ? roomIndex : formIndex]?.name}</span>

            <PassengerFormOptions
                roomIndex={roomIndex ?? ""}
                formIndex={formIndex}
                formType={formType}
                IDcard={IDcard}
                passengerType={passengerType}
                handleIDCardChange={handleIDCardChange}
                selectedCountry={watch(`${nestedName}passengers.${formIndex}.nationality`)}
            >
                {formType === "hotel" && totalPersons[formIndex] - formCount[formIndex]?.capacity - infant?.[formIndex]?.length > 0 ? (
                    <Grid
                        className="passengers-forms__extend-capacity"
                        container
                        item
                        xs="auto"
                        direction="column"
                        justifyContent="center"
                        alignItems="center"
                    >
                        <span>{totalPersons[formIndex] - formCount[formIndex]?.capacity - infant?.[formIndex]?.length} نفر اضافه</span>
                        {numberWithCommas(extendPrice)}
                    </Grid>
                ) : null}
            </PassengerFormOptions>

            <PassengerForm
                fieldList={
                    formType === "flight"
                        ? FLIGHT_FIELD_LIST
                        : formType === "intFlight"
                          ? INT_FLIGHT_FIELD_LIST
                          : formType === "intHotel"
                            ? INT_HOTEL_FIELD_LIST
                            : HOTEL_FIELD_LIST
                }
                roomIndex={roomIndex ?? ""}
                formIndex={formIndex}
                IDcard={IDcard}
                registerOptions={formValidations}
                nestedName={nestedName}
                {...{ control, register, defaultValues, watch, setValue, getValues, resetField, errors }}
            >
                {formType === "hotel" && (
                    <RoomsCount
                        handleChange={handleRoomCountChange}
                        defaultValue={hotelPassengers?.passengers && [hotelPassengers?.passengers[formIndex]?.roomsCount]}
                        multiRoom={false}
                        formIndex={formIndex}
                        validation={formCount[formIndex].capacity + formCount[formIndex].extraCapacity}
                        warningMessage={t("hotelMessage4")}
                        halfPayRule={formCount[formIndex]?.halfPayRule}
                    />
                )}
            </PassengerForm>
        </Grid>
    );
}

export default PassengersFormContainer;
